import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { Button, FormGroup, Label, Input, FormError, Card, CardBody } from '../components/UI';
import api, { setAuth } from '../utils/api';
import toast from 'react-hot-toast';

const Wrapper = styled.div`
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--white) 100%);
  padding: 24px;
`;
const Box = styled.div` width: min(420px, 100%); `;
const Logo = styled.div`
  font-family: var(--font-display); font-size: 1.8rem; font-weight: 700;
  color: var(--primary); text-align: center; margin-bottom: 8px;
  span { color: var(--primary-light); }
`;

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.email) e.email = 'Email is required';
    if (!form.password) e.password = 'Password is required';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const d = await api.post('/auth/login', form);
      login(d.token, d.user);
      toast.success(`Welcome back, ${d.user.name.split(' ')[0]}!`);
      const redirects = { admin: '/admin', vendor: '/vendor', customer: '/' };
      navigate(redirects[d.user.role] || '/');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <Wrapper>
      <Box>
        <Logo>Ameria<span>vis</span></Logo>
        <p style={{ textAlign:'center', color:'var(--gray-500)', marginBottom:28, fontSize:'0.9rem' }}>
          Sign in to your account
        </p>
        <Card>
          <CardBody>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Email Address</Label>
                <Input type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm(f=>({...f, email:e.target.value}))} $error={!!errors.email} />
                {errors.email && <FormError>{errors.email}</FormError>}
              </FormGroup>
              <FormGroup>
                <Label>Password</Label>
                <Input type="password" placeholder="Your password" value={form.password} onChange={e => setForm(f=>({...f, password:e.target.value}))} $error={!!errors.password} />
                {errors.password && <FormError>{errors.password}</FormError>}
              </FormGroup>
              <Button type="submit" $block disabled={loading} style={{ marginTop:8 }}>
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardBody>
        </Card>
        <p style={{ textAlign:'center', marginTop:20, fontSize:'0.9rem', color:'var(--gray-600)' }}>
          Don't have an account? <Link to="/register" style={{ color:'var(--primary)', fontWeight:500 }}>Register</Link>
        </p>
        <p style={{ textAlign:'center', marginTop:8 }}>
          <Link to="/" style={{ fontSize:'0.85rem', color:'var(--gray-400)' }}>← Back to Store</Link>
        </p>
      </Box>
    </Wrapper>
  );
}

export function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name) e.name = 'Name is required';
    if (!form.email) e.email = 'Email is required';
    if (!form.password || form.password.length < 6) e.password = 'Password must be at least 6 characters';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const d = await api.post('/auth/register', { name: form.name, email: form.email, password: form.password });
      login(d.token, d.user);
      toast.success('Account created! Welcome to Ameriavis!');
      navigate('/');
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <Wrapper>
      <Box>
        <Logo>Ameria<span>vis</span></Logo>
        <p style={{ textAlign:'center', color:'var(--gray-500)', marginBottom:28, fontSize:'0.9rem' }}>Create your account</p>
        <Card>
          <CardBody>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Full Name</Label>
                <Input placeholder="John Doe" value={form.name} onChange={e => setForm(f=>({...f, name:e.target.value}))} $error={!!errors.name} />
                {errors.name && <FormError>{errors.name}</FormError>}
              </FormGroup>
              <FormGroup>
                <Label>Email Address</Label>
                <Input type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm(f=>({...f, email:e.target.value}))} $error={!!errors.email} />
                {errors.email && <FormError>{errors.email}</FormError>}
              </FormGroup>
              <FormGroup>
                <Label>Password</Label>
                <Input type="password" placeholder="Min 6 characters" value={form.password} onChange={e => setForm(f=>({...f, password:e.target.value}))} $error={!!errors.password} />
                {errors.password && <FormError>{errors.password}</FormError>}
              </FormGroup>
              <FormGroup>
                <Label>Confirm Password</Label>
                <Input type="password" placeholder="Repeat password" value={form.confirmPassword} onChange={e => setForm(f=>({...f, confirmPassword:e.target.value}))} $error={!!errors.confirmPassword} />
                {errors.confirmPassword && <FormError>{errors.confirmPassword}</FormError>}
              </FormGroup>
              <Button type="submit" $block disabled={loading} style={{ marginTop:8 }}>
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
          </CardBody>
        </Card>
        <p style={{ textAlign:'center', marginTop:20, fontSize:'0.9rem', color:'var(--gray-600)' }}>
          Already have an account? <Link to="/login" style={{ color:'var(--primary)', fontWeight:500 }}>Sign In</Link>
        </p>
      </Box>
    </Wrapper>
  );
}
